/* Dawn Lamps: move the native Shopify Inbox launcher and panel without replacing chat. */
(() => {
  'use strict';
  if (window.__dawnDraggableChat) return;
  window.__dawnDraggableChat = true;
  const storageKey = 'dawn-chat-position-v1';
  const bound = new WeakSet();
  const watched = new WeakSet();
  const controllers = new Set();
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem(storageKey) || '{}') || {}; } catch (_) {}
  if (typeof saved !== 'object' || Array.isArray(saved)) saved = {};
  const mode = () => matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
  const readPosition = key => {
    const p = saved[key];
    return p && Number.isFinite(p.x) && Number.isFinite(p.y)
      ? { x: Math.max(0, Math.min(1, p.x)), y: Math.max(0, Math.min(1, p.y)) } : null;
  };
  const css = `
    [data-dawn-drag] { touch-action: none !important; user-select: none !important; -webkit-user-select: none !important; cursor: grab !important; }
    [data-dawn-drag-active] { cursor: grabbing !important; }
    .activator[data-dawn-drag-active] { scale: 1 !important; }
    [data-dawn-drag-moving] { transition: none !important; animation: none !important; }
    @media (max-width: 749px) { .panel[data-dawn-chat-positioned] { width: calc(100% - 24px) !important; } }
    .panel__header[data-dawn-drag]::after { content: ''; position: absolute; width: 28px; height: 3px; border-radius: 4px; background: currentColor; opacity: .24; left: calc(50% - 14px); top: 11px; pointer-events: none; }
  `;
  const addStyle = root => {
    if (root.querySelector('style[data-dawn-chat-style]')) return;
    const style = document.createElement('style');
    style.dataset.dawnChatStyle = '';
    style.textContent = css;
    root.append(style);
  };
  function bind(host, handle, element, kind) {
    if (!handle || !element || bound.has(handle)) return;
    bound.add(handle);
    addStyle(handle.getRootNode());
    addStyle(element.getRootNode());
    handle.dataset.dawnDrag = kind;
    if (!handle.hasAttribute('title')) handle.title = kind === 'launcher'
      ? 'Click to chat · Drag to move' : 'Drag here to move chat';
    let drag = null;
    let suppressUntil = 0;
    let lastMode = mode();
    let position = readPosition(`${kind}-${lastMode}`);
    let raf = 0;
    const original = ['left', 'right', 'top', 'bottom', 'margin'].map(name =>
      [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
    function bounds() {
      const hostRect = host.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const visual = window.visualViewport;
      const left = Math.max(hostRect.left, visual?.offsetLeft || 0);
      const top = Math.max(hostRect.top, visual?.offsetTop || 0);
      const right = Math.min(hostRect.right, left + (visual?.width || document.documentElement.clientWidth));
      const bottom = Math.min(hostRect.bottom, top + (visual?.height || innerHeight));
      const gap = 12;
      const minX = left + Math.min(gap, Math.max(0, (right - left - rect.width) / 2));
      const minY = top + Math.min(gap, Math.max(0, (bottom - top - rect.height) / 2));
      return { hostRect, rect, minX, minY,
        maxX: Math.max(minX, right - rect.width - gap),
        maxY: Math.max(minY, bottom - rect.height - gap) };
    }
    function place(x, y, remember) {
      const b = bounds();
      if (!b.rect.width || !b.rect.height) return;
      if (kind === 'panel') element.setAttribute('data-dawn-chat-positioned', '');
      x = Math.max(b.minX, Math.min(b.maxX, x));
      y = Math.max(b.minY, Math.min(b.maxY, y));
      const scaleX = b.hostRect.width / (host.clientWidth || b.hostRect.width) || 1;
      const scaleY = b.hostRect.height / (host.clientHeight || b.hostRect.height) || 1;
      const styles = { left: `${(x - b.hostRect.left) / scaleX}px`, top: `${(y - b.hostRect.top) / scaleY}px`, right: 'auto', bottom: 'auto', margin: '0' };
      for (const [name, value] of Object.entries(styles)) {
        if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value, 'important');
      }
      if (remember) position = {
        x: b.maxX > b.minX ? (x - b.minX) / (b.maxX - b.minX) : .5,
        y: b.maxY > b.minY ? (y - b.minY) / (b.maxY - b.minY) : .5
      };
    }
    function restore() {
      if (drag || !element.isConnected) return;
      if (mode() !== lastMode) {
        lastMode = mode(); position = readPosition(`${kind}-${lastMode}`);
        element.removeAttribute('data-dawn-chat-positioned');
        for (const [name, value, priority] of original) {
          if (value) element.style.setProperty(name, value, priority); else element.style.removeProperty(name);
        }
      }
      if (!position) return;
      const b = bounds();
      place(b.minX + position.x * (b.maxX - b.minX), b.minY + position.y * (b.maxY - b.minY), false);
    }
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(restore); };
    const controller = { element, schedule };
    controllers.add(controller);
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    handle.addEventListener('pointerdown', event => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (kind === 'panel' && event.composedPath().some(node => node !== handle && node instanceof Element && node.matches('button,a,input,textarea,select,[role="button"],shopify-sign-in-button,shopify-chat-icon-button'))) return;
      restore();
      const r = element.getBoundingClientRect();
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: r.left, top: r.top, moved: false };
      handle.setAttribute('data-dawn-drag-active', '');
      try { handle.setPointerCapture(event.pointerId); } catch (_) {}
    }, true);
    handle.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 7) return;
      drag.moved = true;
      element.setAttribute('data-dawn-drag-moving', '');
      place(drag.left + dx, drag.top + dy, true);
      event.preventDefault(); event.stopPropagation();
    }, { capture: true, passive: false });
    function finish(event) {
      if (!drag || (event.pointerId !== undefined && drag.id !== event.pointerId)) return;
      const previous = drag; drag = null;
      handle.removeAttribute('data-dawn-drag-active');
      element.removeAttribute('data-dawn-drag-moving');
      if (previous.moved) {
        suppressUntil = performance.now() + 500;
        event.preventDefault(); event.stopPropagation();
        saved[`${kind}-${mode()}`] = position;
        try { localStorage.setItem(storageKey, JSON.stringify(saved)); } catch (_) {}
      }
      try { if (handle.hasPointerCapture(previous.id)) handle.releasePointerCapture(previous.id); } catch (_) {}
      schedule();
    }
    handle.addEventListener('pointerup', finish, true);
    handle.addEventListener('pointercancel', finish, true);
    handle.addEventListener('lostpointercapture', finish, true);
    handle.addEventListener('click', event => {
      if (event.detail !== 0 && performance.now() < suppressUntil) {
        event.preventDefault(); event.stopImmediatePropagation(); suppressUntil = 0;
      }
    }, true);
    element.addEventListener('transitionend', schedule);
    schedule();
    controller.cleanup = () => { observer.disconnect(); cancelAnimationFrame(raf); controllers.delete(controller); };
  }
  let scanQueued = false;
  function scan() {
    scanQueued = false;
    for (const controller of controllers) if (!controller.element.isConnected) controller.cleanup();
    for (const host of document.querySelectorAll('shopify-chat')) {
      if (!host.shadowRoot || host.getAttribute('mode') === 'managed') continue;
      const root = host.shadowRoot;
      const roots = [];
      const collect = r => { roots.push(r); for (const e of r.querySelectorAll('*')) if (e.shadowRoot) collect(e.shadowRoot); };
      collect(root);
      const panel = roots.map(r => r.querySelector('.panel')).find(Boolean);
      for (const r of roots) {
        bind(host, r.querySelector('button.activator'), r.querySelector('.activator-nudge'), 'launcher');
        bind(host, r.querySelector('.panel__header'), panel, 'panel');
        if (!watched.has(r)) {
          watched.add(r);
          new MutationObserver(queueScan).observe(r, { childList: true, subtree: true });
        }
      }
      if (!watched.has(host)) {
        watched.add(host);
        new MutationObserver(() => { queueScan(); for (const c of controllers) c.schedule(); })
          .observe(host, { attributes: true, attributeFilter: ['open', 'view', 'shell-ready'] });
      }
    }
  }
  function queueScan() { if (!scanQueued) { scanQueued = true; setTimeout(scan, 50); } }
  function resize() { for (const c of controllers) c.schedule(); }
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });
  addEventListener('resize', resize, { passive: true });
  addEventListener('pageshow', queueScan);
  document.addEventListener('visibilitychange', queueScan);
  visualViewport?.addEventListener('resize', resize, { passive: true });
  visualViewport?.addEventListener('scroll', resize, { passive: true });
  customElements.whenDefined('shopify-chat').then(queueScan);
  customElements.whenDefined('shopify-agent-runtime').then(queueScan);
  queueScan();
  // Shadow attachment itself has no mutation event. Cover delayed app startup.
  let attempts = 0;
  const startup = setInterval(() => { queueScan(); if (++attempts >= 30) clearInterval(startup); }, 1000);
})();
